import { useEffect, useMemo, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createSystemUser,
  listSystemRoles,
  listSystemUsers,
  updateSystemRolePermissions,
  updateSystemUser,
  updateSystemUserRole,
  updateSystemUserStatus,
  type SystemRoleItem,
  type SystemUserItem,
} from "@/lib/api";

type NewUserForm = {
  fullName: string;
  email: string;
  roleCode: string;
  password: string;
};

type EditUserForm = {
  fullName: string;
  email: string;
  password: string;
};

const roleColorByCode: Record<string, string> = {
  admin: "bg-blue-50 border-blue-100 text-blue-700",
  operator: "bg-emerald-50 border-emerald-100 text-emerald-700",
  cashier: "bg-amber-50 border-amber-100 text-amber-700",
  field_reader: "bg-rose-50 border-rose-100 text-rose-700",
  readonly: "bg-gray-50 border-gray-200 text-gray-700",
};

const avatarColorPalette = ["bg-blue-600", "bg-emerald-600", "bg-amber-600", "bg-rose-600", "bg-violet-600", "bg-slate-600"];

const roleModuleOptions = [
  { code: "dashboard", label: "Dashboard" },
  { code: "clientes", label: "Clientes" },
  { code: "facturacion", label: "Facturación" },
  { code: "operaciones", label: "Operaciones" },
  { code: "control_lecturas", label: "Control de Lecturas" },
  { code: "prestamos", label: "Préstamos" },
  { code: "documentacion_apr", label: "Documentación APR" },
  { code: "bodega", label: "Bodega" },
  { code: "reportes", label: "Reportes" },
  { code: "configuracion", label: "Configuración" },
  { code: "modulo_terreno", label: "Módulo Terreno" },
];

export default function UsuariosTab() {
  const { session, profile } = useSession();
  // Plan desde el profile (siempre fresco) con fallback a la sesión
  const planCode: string | null = profile?.planCode ?? session?.planCode ?? null;
  const planFeatures: string[] = (profile?.planFeatures?.length ? profile.planFeatures : null) ?? session?.planFeatures ?? [];
  const hasFullAccess = !planCode || planCode === "full" || planCode === "permanent";

  const [users, setUsers] = useState<SystemUserItem[]>([]);
  const [roles, setRoles] = useState<SystemRoleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<SystemUserItem | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<SystemRoleItem | null>(null);
  const [roleModulesDraft, setRoleModulesDraft] = useState<string[]>([]);
  const [newUser, setNewUser] = useState<NewUserForm>({
    fullName: "",
    email: "",
    roleCode: "operator",
    password: "",
  });
  const [editUser, setEditUser] = useState<EditUserForm>({
    fullName: "",
    email: "",
    password: "",
  });

  const activeUsers = useMemo(() => users.filter((user) => user.status === "active").length, [users]);
  const availableRoleModules = useMemo(() => {
    const fromRoles = roles.flatMap((role) => role.modules ?? []);
    const known = roleModuleOptions.map((item) => item.code);
    const all = Array.from(new Set([...known, ...fromRoles])).sort((a, b) => a.localeCompare(b));
    // Restringir a los módulos del plan contratado
    if (!hasFullAccess && planFeatures.length > 0) {
      return all.filter((m) => planFeatures.includes(m));
    }
    return all;
  }, [roles, hasFullAccess, planFeatures]);

  useEffect(() => {
    let cancelled = false;
    if (!session) return;

    async function load(): Promise<void> {
      setLoading(true);
      setError("");
      try {
        const [nextUsers, nextRoles] = await Promise.all([
          listSystemUsers(session),
          listSystemRoles(session),
        ]);
        if (cancelled) return;
        setUsers(nextUsers);
        setRoles(nextRoles);
        if (nextRoles.length > 0) {
          setNewUser((prev) => ({ ...prev, roleCode: prev.roleCode || nextRoles[0].code }));
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar usuarios y roles");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  async function handleToggleStatus(user: SystemUserItem): Promise<void> {
    if (!session) return;
    setSaving(true);
    setError("");
    try {
      const nextStatus = user.status === "active" ? "inactive" : "active";
      await updateSystemUserStatus(session, user.id, nextStatus);
      setUsers((prev) => prev.map((row) => (row.id === user.id ? { ...row, status: nextStatus } : row)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo actualizar estado");
    } finally {
      setSaving(false);
    }
  }

  async function handleRoleChange(userId: string, roleCode: string): Promise<void> {
    if (!session) return;
    setSaving(true);
    setError("");
    try {
      await updateSystemUserRole(session, userId, roleCode);
      const nextRole = roles.find((role) => role.code === roleCode) ?? null;
      setUsers((prev) => prev.map((row) => (row.id === userId ? { ...row, role: nextRole ? { code: nextRole.code, name: nextRole.name } : null } : row)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo actualizar rol");
    } finally {
      setSaving(false);
    }
  }

  function openRoleEditor(role: SystemRoleItem): void {
    setEditingRole(role);
    setRoleModulesDraft((role.modules ?? []).slice().sort((a, b) => a.localeCompare(b)));
  }

  function closeRoleEditor(): void {
    setEditingRole(null);
    setRoleModulesDraft([]);
  }

  function toggleDraftModule(moduleCode: string): void {
    setRoleModulesDraft((current) => {
      if (current.includes(moduleCode)) {
        return current.filter((item) => item !== moduleCode);
      }
      return [...current, moduleCode].sort((a, b) => a.localeCompare(b));
    });
  }

  async function saveRoleModules(): Promise<void> {
    if (!session || !editingRole) return;
    setSaving(true);
    setError("");
    try {
      await updateSystemRolePermissions(session, editingRole.code, roleModulesDraft);
      const refreshedRoles = await listSystemRoles(session);
      setRoles(refreshedRoles);
      setUsers((prev) =>
        prev.map((user) => {
          if (!user.role?.code) return user;
          const refreshedRole = refreshedRoles.find((role) => role.code === user.role?.code);
          if (!refreshedRole) return user;
          return { ...user, role: { code: refreshedRole.code, name: refreshedRole.name } };
        }),
      );
      closeRoleEditor();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron guardar permisos");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateUser(): Promise<void> {
    if (!session) return;
    if (!newUser.fullName.trim() || !newUser.email.trim() || !newUser.roleCode) {
      setError("Completa nombre, correo y rol.");
      return;
    }
    if (newUser.password.trim() && newUser.password.trim().length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const result = await createSystemUser(session, {
        fullName: newUser.fullName.trim(),
        email: newUser.email.trim(),
        roleCode: newUser.roleCode,
        password: newUser.password.trim() || undefined,
      });
      const refreshed = await listSystemUsers(session);
      setUsers(refreshed);
      setGeneratedPassword(result.temporaryPassword);
      setShowModal(false);
      setNewUser({ fullName: "", email: "", roleCode: roles[0]?.code ?? "operator", password: "" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo crear usuario");
    } finally {
      setSaving(false);
    }
  }

  function openEditUser(user: SystemUserItem): void {
    setEditingUser(user);
    setEditUser({
      fullName: user.fullName,
      email: user.email,
      password: "",
    });
    setShowEditUserModal(true);
  }

  function closeEditUser(): void {
    setShowEditUserModal(false);
    setEditingUser(null);
    setEditUser({ fullName: "", email: "", password: "" });
  }

  async function handleUpdateUser(): Promise<void> {
    if (!session || !editingUser) return;
    const fullName = editUser.fullName.trim();
    const email = editUser.email.trim().toLowerCase();
    if (!fullName || !email) {
      setError("Nombre y correo son obligatorios.");
      return;
    }
    if (editUser.password.trim() && editUser.password.trim().length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const updated = await updateSystemUser(session, editingUser.id, {
        fullName,
        email,
        password: editUser.password.trim() || undefined,
      });
      setUsers((prev) => prev.map((row) => (row.id === editingUser.id ? updated : row)));
      closeEditUser();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo actualizar usuario");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      {generatedPassword ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Usuario creado. Contraseña temporal: <span className="font-semibold">{generatedPassword}</span>
          <button className="ml-3 text-amber-700 underline" onClick={() => setGeneratedPassword(null)}>Cerrar</button>
        </div>
      ) : null}

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50">
              <i className="ri-team-line text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Usuarios del Sistema</h2>
              <p className="text-xs text-gray-400">{activeUsers} usuarios activos</p>
            </div>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-add-line" />
            </div>
            Nuevo usuario
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500 p-3">Cargando usuarios...</div>
        ) : (
          <div className="space-y-2">
            {users.map((user, index) => {
              const initials = getInitials(user.fullName);
              const avatarColor = avatarColorPalette[index % avatarColorPalette.length];
              return (
                <div key={user.id} className="flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full ${avatarColor} text-white text-xs font-bold flex-shrink-0`}>
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800">{user.fullName}</div>
                    <div className="text-xs text-gray-400">{user.email}</div>
                  </div>
                  <div className="hidden md:block min-w-[180px]">
                    <select
                      value={user.role?.code ?? ""}
                      onChange={(event) => void handleRoleChange(user.id, event.target.value)}
                      disabled={saving}
                      className="w-full px-2.5 py-1 text-xs border border-gray-200 rounded-full font-medium bg-white"
                    >
                      <option value="">Sin rol</option>
                      {roles.map((role) => (
                        <option key={role.code} value={role.code}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="text-xs text-gray-400 hidden lg:block whitespace-nowrap">{toLastAccessLabel(user.lastLoginAt)}</div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditUser(user)}
                      disabled={saving}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 cursor-pointer disabled:opacity-60"
                      title="Editar usuario"
                    >
                      <i className="ri-pencil-line" />
                    </button>
                    <button
                      onClick={() => void handleToggleStatus(user)}
                      disabled={saving}
                      className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${user.status === "active" ? "bg-emerald-500" : "bg-gray-300"}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${user.status === "active" ? "left-5" : "left-0.5"}`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50">
            <i className="ri-shield-user-line text-amber-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Roles y Permisos</h2>
            <p className="text-xs text-gray-400">Niveles de acceso disponibles en el sistema</p>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500 p-3">Cargando roles...</div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {roles.map((role) => (
              <div key={role.id} className={`p-4 rounded-lg border ${roleColorByCode[role.code] ?? "bg-gray-50 border-gray-200 text-gray-700"}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="font-semibold text-sm">{role.name}</div>
                  <button
                    onClick={() => openRoleEditor(role)}
                    className="text-xs px-2 py-1 rounded-md border border-white/60 bg-white/70 hover:bg-white cursor-pointer"
                  >
                    Editar permisos
                  </button>
                </div>
                <div className="text-xs opacity-70 mb-2">{role.description}</div>
                <div className="flex flex-wrap gap-1">
                  {role.modules
                    .filter((m) => hasFullAccess || planFeatures.length === 0 || planFeatures.includes(m))
                    .map((module) => (
                      <span key={module} className="px-2 py-0.5 bg-white/60 rounded text-xs font-medium">{moduleLabel(module)}</span>
                    ))}
                  {role.modules.filter((m) => hasFullAccess || planFeatures.length === 0 || planFeatures.includes(m)).length === 0
                    ? <span className="text-xs opacity-80">Sin módulos asignados</span>
                    : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-800">Permisos de rol</h3>
                <p className="text-xs text-gray-500">{editingRole.name}</p>
              </div>
              <button onClick={closeRoleEditor} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-400">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-5">
              {availableRoleModules.map((moduleCode) => {
                const selected = roleModulesDraft.includes(moduleCode);
                return (
                  <label
                    key={moduleCode}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer ${
                      selected ? "border-blue-300 bg-blue-50 text-blue-700" : "border-gray-200 bg-white text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleDraftModule(moduleCode)}
                      className="accent-blue-600"
                    />
                    <span>{moduleLabel(moduleCode)}</span>
                  </label>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button onClick={closeRoleEditor} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button disabled={saving} onClick={() => void saveRoleModules()} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap disabled:opacity-60">
                {saving ? "Guardando..." : "Guardar permisos"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-gray-800">Nuevo Usuario</h3>
              <button onClick={() => setShowModal(false)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-400">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Nombre completo</label>
                <input
                  type="text"
                  value={newUser.fullName}
                  onChange={(event) => setNewUser((prev) => ({ ...prev, fullName: event.target.value }))}
                  placeholder="Ej: Juan Pérez"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Correo electrónico</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(event) => setNewUser((prev) => ({ ...prev, email: event.target.value }))}
                  placeholder="usuario@aprquechurehue.cl"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Rol</label>
                <select
                  value={newUser.roleCode}
                  onChange={(event) => setNewUser((prev) => ({ ...prev, roleCode: event.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 transition-colors bg-white"
                >
                  {roles.map((role) => (
                    <option key={role.code} value={role.code}>{role.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Contraseña inicial (opcional)</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(event) => setNewUser((prev) => ({ ...prev, password: event.target.value }))}
                  placeholder="Mínimo 8 caracteres. Si va vacío, se usa una por defecto"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button disabled={saving} onClick={() => void handleCreateUser()} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap disabled:opacity-60">
                {saving ? "Creando..." : "Crear usuario"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-gray-800">Editar Usuario</h3>
              <button onClick={closeEditUser} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-400">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Nombre completo</label>
                <input
                  type="text"
                  value={editUser.fullName}
                  onChange={(event) => setEditUser((prev) => ({ ...prev, fullName: event.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Correo electrónico</label>
                <input
                  type="email"
                  value={editUser.email}
                  onChange={(event) => setEditUser((prev) => ({ ...prev, email: event.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Nueva contraseña (opcional)</label>
                <input
                  type="password"
                  value={editUser.password}
                  onChange={(event) => setEditUser((prev) => ({ ...prev, password: event.target.value }))}
                  placeholder="Dejar vacío para mantener la actual"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={closeEditUser} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button disabled={saving} onClick={() => void handleUpdateUser()} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap disabled:opacity-60">
                {saving ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getInitials(fullName: string): string {
  return fullName
    .split(" ")
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "US";
}

function toLastAccessLabel(value: string | null): string {
  if (!value) return "Nunca";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Nunca";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.max(1, Math.round(diffMs / 60000));
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `Hace ${diffHour} h`;
  const diffDay = Math.round(diffHour / 24);
  if (diffDay === 1) return "Ayer";
  return `Hace ${diffDay} días`;
}

function moduleLabel(code: string): string {
  const labels: Record<string, string> = {
    dashboard: "Dashboard",
    clientes: "Clientes",
    facturacion: "Facturación",
    operaciones: "Operaciones",
    control_lecturas: "Control de Lecturas",
    prestamos: "Préstamos",
    documentacion_apr: "Documentación APR",
    bodega: "Bodega",
    reportes: "Reportes",
    configuracion: "Configuración",
    modulo_terreno: "Módulo Terreno",
  };
  return labels[code] ?? code;
}
